import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Device } from '@twilio/voice-sdk';
import { get_api, post_api } from '../../../redux/api_funcs';
import { set_notification_variable } from '../../../redux/action_creators/home_actions';
import { get_api_url } from '../../../utils/urls';
import { getCookie } from '../../../util_functions';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import MytNotification from '../../../myt_chat/notification';
import IncomingCallPill from './IncomingCallPill';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

// Server issues Voice tokens with a 1 h TTL (GenVoiceToken). Refresh well
// before expiry so `<Client>` routing never lapses.
const TOKEN_REFRESH_MS = 50 * 60 * 1000;

// Mount this once at the app shell (e.g. inside Home). It keeps a Twilio
// Voice Device registered for as long as the agent is marked available,
// regardless of which CMS page they're on, so incoming <Client> TwiML can
// always reach the browser. Renders the IncomingCallPill globally.
export default function GlobalIncomingCallManager() {
  const dispatch = useDispatch();
  const [available, setAvailable] = useState(false);
  // 'idle' (not available) | 'registering' | 'registered' | 'error'
  const [deviceState, setDeviceState] = useState('idle');
  const [deviceError, setDeviceError] = useState(null);
  const [deviceIdentity, setDeviceIdentity] = useState(null);

  const [pendingByCallSid, setPendingByCallSid] = useState({});
  const [activeIncoming, setActiveIncoming] = useState(null);
  const [incomingStatus, setIncomingStatus] = useState(null);
  const [incomingMuted, setIncomingMuted] = useState(false);

  // Conference / multi-agent state.
  // - `participantsByCallUUID` mirrors the server's Redis SET via the
  //   `participant_changed` relay so we can render the End/Leave button
  //   text and disable the Join icon for users already in.
  // - `unlockedSet` tracks which call_uuids have been promoted to a
  //   conference (server emits 'unlocked' on upgrade_done).
  const [participantsByCallUUID, setParticipantsByCallUUID] = useState({});
  const [unlockedSet, setUnlockedSet] = useState(() => new Set());
  const currentUserUUID = getCookie('care_uuid') || '';

  const deviceRef = useRef(null);
  const activeCallRef = useRef(null);
  // Held in a ref so the Device-lifecycle useEffect doesn't re-fire (and
  // teardown the Device!) every time pendingByCallSid mutates.
  const attachIncomingCallRef = useRef(null);

  // Broadcast a flag used by GlobalOutboundCallManager to block outbound
  // dials while an incoming call is active. We're "active" when there is
  // an activeIncoming row AND the status is non-terminal — i.e., ringing,
  // connecting, or connected. Terminal statuses (ended/rejected/failed/
  // disconnected) release the flag so the agent can dial out again.
  useEffect(() => {
    const TERMINAL = new Set(['ended', 'rejected', 'failed', 'disconnected']);
    const isActive = !!activeIncoming && !!incomingStatus && !TERMINAL.has(incomingStatus);
    if (typeof window !== 'undefined') {
      window.__twilioIncomingActive = isActive;
      window.dispatchEvent(
        new CustomEvent('twilio-incoming-call-active', { detail: { active: isActive } })
      );
    }
  }, [activeIncoming, incomingStatus]);

  const stopRing = useCallback((callSid) => {
    if (!callSid) return;
    MytNotification.stopIncomingCallRing(`incoming-call-${callSid}`);
  }, []);

  // The ring is started in home/index with the parent (PSTN) CallSid from the
  // relay, but the SDK exposes both the parent (via customParameters) and the
  // child (call.parameters.CallSid) legs — and either can be missing in some
  // edge cases. Stop every id we know of so we never leave a ring playing.
  const stopAllRings = useCallback(
    (sids) => {
      if (!sids) return;
      const seen = new Set();
      for (const s of sids) {
        if (!s || seen.has(s)) continue;
        seen.add(s);
        stopRing(s);
      }
    },
    [stopRing]
  );

  const broadcastOngoingRefresh = useCallback(() => {
    window.dispatchEvent(new CustomEvent('twilio-ongoing-calls-refresh'));
  }, []);

  const attachIncomingCall = useCallback(
    (call) => {
      // Don't ring the agent if they're already on an outbound call. We
      // auto-reject the SDK call so Twilio's <Dial> action URL fires with
      // a non-completed status — the backend then routes the PSTN caller
      // to voicemail just like the no-answer path. This keeps the agent
      // focused on their current call without dropping the inbound caller
      // silently.
      if (typeof window !== 'undefined' && window.__twilioOutboundActive) {
        try {
          call.reject?.();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('global_incoming.auto_reject_failed', e);
        }
        return;
      }

      activeCallRef.current = call;

      // The SDK's call.parameters.CallSid is the child (Client) leg SID,
      // but the ring was started with the parent (PSTN) SID from the relay.
      // We track BOTH so stopRing hits whichever the ring was keyed on —
      // ParentCallSid is the expected id, but it can be missing if the TwiML
      // <Parameter> didn't propagate, in which case the ring was keyed on
      // whatever call_sid the relay carried (which equals the child here).
      const parentSid = call?.customParameters?.get?.('ParentCallSid');
      const childSid =
        call?.parameters?.CallSid || call?.customParameters?.get?.('CallSid') || null;
      const ringSids = [parentSid, childSid].filter(Boolean);
      const sid = parentSid || childSid;
      const meta = ringSids.map((s) => pendingByCallSid[s]).find(Boolean) || null;

      // Conference dial-in for a joiner: when the user clicked Join on the
      // Ongoing Calls table, we stashed the target call_uuid before the REST
      // call, and the next SDK incoming is the conference dial-in for that
      // call. Joiner pills don't show the lock icon (they're not the host).
      let joinTargetUUID = null;
      if (typeof window !== 'undefined' && window.__twilioJoinTarget) {
        joinTargetUUID = window.__twilioJoinTarget;
        window.__twilioJoinTarget = null;
        // Cancel the join-watchdog timeout — the SDK incoming arrived,
        // so the "we never connected" notification shouldn't fire.
        if (window.__twilioJoinTargetTimeout) {
          clearTimeout(window.__twilioJoinTargetTimeout);
          window.__twilioJoinTargetTimeout = null;
        }
      }

      const callUUID = (meta && meta.call_uuid) || joinTargetUUID || null;
      const isJoiner = !!joinTargetUUID;

      setActiveIncoming({
        callSid: sid || null,
        callUUID,
        isJoiner,
        ringSids,
        fromNumber: (meta && meta.from_number) || call?.parameters?.From || call?.from || 'Unknown',
        studentName: meta && meta.student_name ? meta.student_name : null
      });
      setIncomingStatus(isJoiner ? 'connecting' : 'ringing');
      setIncomingMuted(false);

      // Attach all listeners BEFORE accept() — Twilio's SDK can fire the
      // 'accept' event synchronously during the call.accept() invocation;
      // listeners attached after that miss the event and the pill never
      // transitions to 'connected'.
      call.on('accept', () => {
        setIncomingStatus('connected');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('disconnect', () => {
        setIncomingStatus('ended');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('cancel', () => {
        setIncomingStatus('ended');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('reject', () => {
        setIncomingStatus('rejected');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('error', (err) => {
        console.error('global_incoming.error', err);
        setIncomingStatus('failed');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('mute', (isMuted) => setIncomingMuted(!!isMuted));

      // Auto-accept the conference dial-in: the user already opted in by
      // clicking Join, so making them click Accept on the pill again is a
      // wart. The pill still appears so they can mute / leave / end.
      //
      // Audio gotcha: the original click that triggered the join lives in
      // a different turn of the event loop — by the time Twilio dials our
      // browser back and we get here, we're outside the browser's user-
      // gesture window, and Chrome will silently refuse to start the
      // WebRTC remote stream's <audio> element. We resume the SDK's audio
      // context BEFORE calling accept; the click handler in
      // /twilio-agent's joinOngoing also fires a `twilio-warm-audio`
      // event that resumed the same context inside the gesture. Together
      // they cover both first-join and rejoin-after-leave.
      if (isJoiner) {
        try {
          const ctx = deviceRef.current?.audio?.audioContext;
          if (ctx && ctx.state === 'suspended') {
            ctx
              .resume()
              .catch((err) =>
                console.error('global_incoming.audio_context_resume_failed_pre_accept', err)
              );
          }
        } catch (e) {
          console.error('global_incoming.audio_context_lookup_failed', e);
        }
        try {
          call.accept?.();
        } catch (e) {
          console.error('global_incoming.join_auto_accept_failed', e);
        }
      }
    },
    [pendingByCallSid, stopAllRings, broadcastOngoingRefresh]
  );

  useEffect(() => {
    attachIncomingCallRef.current = attachIncomingCall;
  }, [attachIncomingCall]);

  // Resume the SDK's audio context when /twilio-agent's joinOngoing click
  // handler fires `twilio-warm-audio`. The click is the only user gesture
  // we get; by the time Twilio dials our browser back and the SDK fires
  // device.on('incoming') we're outside the browser's gesture window,
  // and Chrome refuses to start the WebRTC remote stream's <audio>. By
  // resuming the AudioContext from inside the click's event-loop turn we
  // satisfy Chrome's autoplay policy ahead of time. Belt-and-braces with
  // the second resume() call inside attachIncomingCall.
  useEffect(() => {
    const handler = () => {
      try {
        const ctx = deviceRef.current?.audio?.audioContext;
        if (ctx && ctx.state === 'suspended') {
          ctx
            .resume()
            .catch((err) => console.error('global_incoming.audio_context_resume_failed_warm', err));
        }
      } catch (e) {
        console.error('global_incoming.audio_context_warm_lookup_failed', e);
      }
    };
    window.addEventListener('twilio-warm-audio', handler);
    return () => window.removeEventListener('twilio-warm-audio', handler);
  }, []);

  // Fetch availability once on mount, then rely on the
  // `twilio-agent-availability-changed` window event. Local toggles dispatch
  // it directly; server-side changes arrive via a chat relay that home/index
  // translates into the same event, so every tab stays in sync without polling.
  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      try {
        const resp = await get_api(getUrl('/v2/twilio_call/availability'), true);
        if (cancelled) return;
        setAvailable(!!resp.data?.data?.available);
      } catch (err) {
        console.error('global_incoming.availability_fetch_failed', err);
      }
    };

    refresh();

    const onAvailabilityChanged = (e) => {
      const next = !!e?.detail?.available;
      setAvailable(next);
    };
    window.addEventListener('twilio-agent-availability-changed', onAvailabilityChanged);

    return () => {
      cancelled = true;
      window.removeEventListener('twilio-agent-availability-changed', onAvailabilityChanged);
    };
  }, []);

  // Twilio Device lifecycle. Registered for the lifetime of the manager
  // (i.e., as long as the user has the home shell mounted), regardless of
  // the `available` flag. `available` controls inbound call ROUTING — the
  // server-side agentsAvailableForCall picks only available users for new
  // PSTN calls. Device registration is a separate concern: it just means
  // "my browser can receive Twilio Client calls when explicitly dialed".
  // Decoupling these lets an unavailable user JOIN an ongoing call (where
  // the server REST-creates a call to client:cms_<them>) without first
  // toggling availability and accidentally inviting a fresh inbound PSTN.
  useEffect(() => {
    let cancelled = false;
    let refreshTimer = null;

    const fetchToken = async () => {
      const resp = await get_api(getUrl('/v2/twilio_call/token'), true);
      return {
        token: resp.data?.data?.token,
        identity: resp.data?.data?.identity
      };
    };

    const scheduleRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(async () => {
        refreshTimer = null;
        if (cancelled) return;
        const device = deviceRef.current;
        if (!device) return;
        try {
          const { token } = await fetchToken();
          if (cancelled || !token || !deviceRef.current) return;
          deviceRef.current.updateToken(token);
          // eslint-disable-next-line no-console
          console.info('[twilio] Device token refreshed');
          scheduleRefresh();
        } catch (err) {
          console.error('global_incoming.device.token_refresh_failed', err);
          // Retry in 1 min; next register effect will also recover.
          refreshTimer = setTimeout(scheduleRefresh, 60 * 1000);
        }
      }, TOKEN_REFRESH_MS);
    };

    const teardown = () => {
      console.log('[twilio] Tearing down Device');

      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }
      const device = deviceRef.current;
      deviceRef.current = null;
      if (!device) return;
      try {
        device.removeAllListeners?.();
        device.destroy();
      } catch (err) {
        console.error('global_incoming.device.destroy_failed', err);
      }
    };

    setDeviceState('registering');
    setDeviceError(null);

    (async () => {
      try {
        const { token, identity } = await fetchToken();
        if (cancelled || !token) {
          if (!cancelled) {
            setDeviceState('error');
            setDeviceError('no token');
          }
          return;
        }

        // eslint-disable-next-line no-console
        console.info('[twilio] Registering Device with identity =', identity);
        if (!cancelled) setDeviceIdentity(identity || null);

        if (deviceRef.current) teardown();

        const device = new Device(token);
        deviceRef.current = device;

        device.on('incoming', (call) => {
          attachIncomingCallRef.current?.(call);
        });
        device.on('error', (err) => {
          console.error('global_incoming.device.error', err);
          if (!cancelled) {
            setDeviceState('error');
            setDeviceError(err?.message || String(err));
          }
        });
        device.on('registered', () => {
          // eslint-disable-next-line no-console
          console.info(
            '[twilio] Device registered for incoming calls — identity =',
            device.identity || identity
          );
          if (!cancelled) setDeviceState('registered');
        });
        device.on('unregistered', () => {
          // eslint-disable-next-line no-console
          console.info('[twilio] Device unregistered');
        });
        device.on('tokenWillExpire', async () => {
          console.log('[twilio] Device token will expire soon, refreshing...');
          // Belt-and-braces: Twilio SDK also warns ~5 min before expiry.
          try {
            const refreshed = await fetchToken();
            if (cancelled || !refreshed.token || !deviceRef.current) return;
            deviceRef.current.updateToken(refreshed.token);
            // eslint-disable-next-line no-console
            console.info('[twilio] Device token refreshed via tokenWillExpire');
          } catch (err) {
            console.error('global_incoming.device.tokenWillExpire_refresh_failed', err);
          }
        });

        await device.register();
        console.log('[twilio] Device register() called');
        scheduleRefresh();
      } catch (err) {
        console.error('global_incoming.device.register_failed', err);
        if (!cancelled) {
          setDeviceState('error');
          setDeviceError(err?.message || String(err));
        }
      }
    })();

    return () => {
      cancelled = true;
      teardown();
    };
    // Mount-only: Device should stay registered across availability toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track relay metadata so the pill can show name, and so we can reconcile
  // with the SDK's incoming event by CallSid.
  useEffect(() => {
    const handler = (item) => {
      if (!item || !item.data) return;
      const { type } = item.data;

      if (type === 'incoming_call') {
        const callSid = item.data.call_sid;
        if (!callSid) return;
        setPendingByCallSid((prev) => ({ ...prev, [callSid]: item.data }));
        broadcastOngoingRefresh();
        return;
      }

      if (type === 'incoming_call_ended') {
        broadcastOngoingRefresh();
        return;
      }

      // Conference upgrade complete. Server has placed the customer +
      // originator legs into a Conference room; the Join button on the
      // ongoing-calls row becomes available, and the originator's pill
      // flips out of locked state.
      if (type === 'unlocked') {
        const callUUID = item.data.call_uuid;
        if (!callUUID) return;
        setUnlockedSet((prev) => {
          if (prev.has(callUUID)) return prev;
          const next = new Set(prev);
          next.add(callUUID);
          return next;
        });
        broadcastOngoingRefresh();
        return;
      }

      // Live participant set update. Fired on every conference join/leave.
      if (type === 'participant_changed') {
        const callUUID = item.data.call_uuid;
        const participants = Array.isArray(item.data.participants) ? item.data.participants : [];
        if (!callUUID) return;
        setParticipantsByCallUUID((prev) => ({ ...prev, [callUUID]: participants }));
      }
    };
    CallbackHandler.shared.registerRelayItemListener(handler);
    return () => {
      CallbackHandler.shared.unregisterRelayItemListener(handler);
    };
  }, [broadcastOngoingRefresh]);

  const acceptIncoming = () => {
    setIncomingStatus('connecting');
    try {
      activeCallRef.current?.accept();
    } catch (err) {
      console.error('global_incoming.accept_failed', err);
      setIncomingStatus('failed');
      // accept() threw — the SDK will not emit 'accept'/'disconnect', so the
      // ring would otherwise play until MytNotification's max-timeout.
      stopAllRings(activeIncoming?.ringSids);
    }
  };

  const rejectIncoming = () => {
    try {
      activeCallRef.current?.reject();
    } catch (err) {
      console.error('global_incoming.reject_failed', err);
    }
    stopAllRings(activeIncoming?.ringSids);
    setIncomingStatus('rejected');
  };

  const toggleIncomingMute = () => {
    const call = activeCallRef.current;
    if (!call) return;
    try {
      const next = !call.isMuted();
      call.mute(next);
      setIncomingMuted(next);
    } catch (err) {
      console.error('global_incoming.mute_failed', err);
    }
  };

  const endIncoming = () => {
    try {
      activeCallRef.current?.disconnect();
    } catch (err) {
      console.error('global_incoming.disconnect_failed', err);
    }
  };

  const unlockIncoming = useCallback(async () => {
    const callUUID = activeIncoming?.callUUID;
    if (!callUUID) {
      dispatch(
        set_notification_variable(
          true,
          'error',
          'Could not resolve this call yet — try again in a moment.'
        )
      );
      return;
    }
    try {
      await post_api(getUrl('/v2/twilio_call/unlock_call'), { call_uuid: callUUID }, true);
      // The 'unlocked' relay flips the icon for everyone; no local update needed.
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('global_incoming.unlock_failed', err);
      const reason =
        err?.response?.data?.reason || err?.message || 'Failed to move call to conference';
      dispatch(set_notification_variable(true, 'error', reason));
    }
  }, [activeIncoming, dispatch]);

  const leaveIncoming = () => {
    // Just drop our SDK leg; conference continues for the remaining legs.
    try {
      activeCallRef.current?.disconnect();
    } catch (err) {
      console.error('global_incoming.leave_failed', err);
    }
  };

  const endForEveryone = useCallback(async () => {
    const callUUID = activeIncoming?.callUUID;
    // Pre-conference (no callUUID): there's nothing for the server to
    // "end for everyone" — local hangup is correct and equivalent to End.
    if (!callUUID) {
      endIncoming();
      return;
    }
    try {
      await post_api(getUrl('/v2/twilio_call/end_ongoing'), { call_uuid: callUUID }, true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('global_incoming.end_for_everyone_failed', err);
      // DON'T fall through to a local disconnect on failure — that would
      // silently behave like Leave (only this user drops, conference
      // continues), which is the opposite of the user's intent.
      const reason =
        err?.response?.data?.reason ||
        err?.message ||
        'Failed to end call for everyone — try again, or click Leave to drop just yourself.';
      dispatch(set_notification_variable(true, 'error', reason));
      return;
    }
    // REST succeeded → server REST-completes the conference; conference-end
    // webhook will tear down our SDK leg too. Local disconnect is a fast-
    // path so the pill closes without waiting for the round-trip.
    try {
      activeCallRef.current?.disconnect();
    } catch (e) {
      /* noop */
    }
  }, [activeIncoming, dispatch]);

  // Terminal pill states auto-close after a short delay so the agent isn't
  // forced to click the X manually. Manual close (onClose) short-circuits.
  useEffect(() => {
    if (!incomingStatus) return undefined;
    if (!['ended', 'rejected', 'failed', 'disconnected'].includes(incomingStatus)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      closeIncomingPill();
    }, 5000);
    return () => clearTimeout(timer);
    // closeIncomingPill is defined below and doesn't change between renders;
    // including it would recreate the timer every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingStatus]);

  const closeIncomingPill = () => {
    stopAllRings(activeIncoming?.ringSids);
    setActiveIncoming(null);
    setIncomingStatus(null);
    activeCallRef.current = null;
    const ringSids = activeIncoming?.ringSids;
    if (ringSids && ringSids.length > 0) {
      setPendingByCallSid((prev) => {
        const next = { ...prev };
        for (const s of ringSids) delete next[s];
        return next;
      });
    }
  };

  const badgeColor =
    deviceState === 'registered'
      ? {
          bg: 'var(--state-success-bg)',
          fg: 'var(--state-success-text)',
          dot: 'var(--state-success)'
        }
      : deviceState === 'registering'
        ? {
            bg: 'var(--state-warning-bg)',
            fg: 'var(--state-warning-text)',
            dot: 'var(--state-warning)'
          }
        : deviceState === 'error'
          ? {
              bg: 'var(--state-error-bg)',
              fg: 'var(--state-error-text)',
              dot: 'var(--state-error)'
            }
          : null; // 'idle' → hidden

  return (
    <>
      {/* {badgeColor && (
        <div
          title={
            deviceError ? `Twilio Device error: ${deviceError}` : `Twilio Device: ${deviceState}`
          }
          style={{
            position: 'fixed',
            bottom: 12,
            right: 12,
            zIndex: 9998,
            padding: '4px 10px',
            borderRadius: 9999,
            background: badgeColor.bg,
            color: badgeColor.fg,
            fontSize: 11,
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.04)'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: 9999,
              background: badgeColor.dot
            }}
          />
          Twilio: {deviceState}
          {deviceIdentity && deviceState === 'registered' && (
            <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> · {deviceIdentity}</span>
          )}
        </div>
      )} */}

      {activeIncoming && incomingStatus
        ? (() => {
            const { callUUID } = activeIncoming;
            const unlocked = !!callUUID && unlockedSet.has(callUUID);
            const participants = (callUUID && participantsByCallUUID[callUUID]) || [];
            const opsCount = participants.filter(
              (p) => typeof p === 'string' && p.startsWith('cms_')
            ).length;
            // Originator = the user this pill belongs to AND who answered the
            // original call. If they joined via the table, isJoiner is true.
            const isOriginator = !activeIncoming.isJoiner;
            return (
              <IncomingCallPill
                fromNumber={activeIncoming.fromNumber}
                studentName={activeIncoming.studentName}
                status={incomingStatus}
                muted={incomingMuted}
                unlocked={unlocked}
                isOriginator={isOriginator}
                participantCount={opsCount || 1}
                onAccept={acceptIncoming}
                onReject={rejectIncoming}
                onToggleMute={toggleIncomingMute}
                onEndCall={endIncoming}
                onUnlock={unlockIncoming}
                onLeave={leaveIncoming}
                onEndForEveryone={endForEveryone}
                onClose={closeIncomingPill}
              />
            );
          })()
        : null}
    </>
  );
}
